import { supabase } from '../supabase';
import {
  describeSessionActivity,
  formatMaskedIp,
  formatSessionLocation,
  getCurrentSessionIdFromAccessToken,
  isMobileDeviceLabel,
  parseDeviceLabel,
} from '../utils/auth/sessionDeviceLabel';
import { decodeAalFromJwt } from '../utils/auth/aalClaim';

/**
 * Account → Security → Sessions data layer.
 * Lists/revokes via SECURITY DEFINER RPCs over auth.sessions.
 * The geo touch (touch-user-session) is DORMANT — nothing calls it — so
 * user_session_geo.city is never written and locationLabel degrades to the
 * IP-derived country only (see the bridge, RD-SESS-033).
 * Revoke audit trail (session_revoked / sessions_revoked_others) is written
 * SERVER-SIDE inside the revoke RPCs themselves (server-derived, tamper-proof)
 * — this file never writes the audit event, only calls the RPC + best-effort
 * refresh-token cleanup.
 */

export async function getCurrentAuthSessionId() {
  const { data: { session } } = await supabase.auth.getSession();
  return getCurrentSessionIdFromAccessToken(session?.access_token) || null;
}

/**
 * This device's own assurance level from its access token ('aal1' | 'aal2' |
 * null). Used to explain an EMPTY session list honestly: at aal2 the server's
 * AAL2 read-gate cannot be the reason, so the session itself must be gone.
 */
export async function getCurrentAuthAal() {
  const { data: { session } } = await supabase.auth.getSession();
  return decodeAalFromJwt(session?.access_token);
}

/**
 * Fire-and-forget geo enrichment for the current session.
 * Dormant: Supabase's edge runtime sends no cf-ipcountry/cf-ipcity, so this
 * wrote nothing (see .build_blocks bridge, RD-SESS-033). Re-wire only when
 * that feature is revived.
 */
export function touchCurrentUserSession() {
  void supabase.functions.invoke('touch-user-session', { body: {} }).catch(() => {});
}

function mapRow(row, currentSessionId, now) {
  const id = row.id;
  const isCurrent = Boolean(currentSessionId && id === currentSessionId);
  const lastActiveAt = row.refreshed_at || row.updated_at || row.created_at || null;
  const deviceLabel = parseDeviceLabel(row.user_agent, { unknownDevice: null }); // null -> the UI shows the translated fallback
  const countryCode = typeof row.country_code === 'string' ? row.country_code.trim().toUpperCase() : null;
  const city = typeof row.city === 'string' ? row.city.trim() : null;
  return {
    id,
    isCurrent,
    deviceLabel,
    isMobile: isMobileDeviceLabel(deviceLabel),
    ipMasked: formatMaskedIp(row.ip_masked),
    countryCode: countryCode || null,
    city: city || null,
    locationLabel: formatSessionLocation({
      city,
      countryCode,
    }),
    lastActiveAt,
    activity: describeSessionActivity(lastActiveAt, { now, isCurrent }),
    createdAt: row.created_at || null,
  };
}

export async function listAccountSessions() {
  const currentSessionId = await getCurrentAuthSessionId();
  const { data, error } = await supabase.rpc('list_own_auth_sessions');
  if (error) throw error;
  const now = Date.now();
  const rows = Array.isArray(data) ? data : [];
  const sessions = rows.map((row) => mapRow(row, currentSessionId, now));
  // Current device first, then by already-sorted last-active from RPC.
  sessions.sort((a, b) => {
    if (a.isCurrent && !b.isCurrent) return -1;
    if (!a.isCurrent && b.isCurrent) return 1;
    return 0;
  });
  return { sessions, currentSessionId };
}

export async function revokeAccountSession(sessionId) {
  if (!sessionId) throw new Error('session_id_required');
  const { data, error } = await supabase.rpc('revoke_own_auth_session', {
    p_session_id: sessionId,
  });
  if (error) throw error;
  // RPC deletes the auth.sessions row (cascades refresh tokens) AND writes
  // the server-derived audit event in the same transaction — nothing left
  // to do here.
  return Boolean(data);
}

export async function revokeOtherAccountSessions({ count } = {}) {
  // RPC first: it's the authoritative delete + the server-derived audit
  // write. If it ran AFTER signOut({ scope: 'others' }), the auth.sessions
  // rows would already be gone and ROW_COUNT (and the audit's count) would
  // read 0. A genuine RPC failure means the action failed — throw.
  const { data, error } = await supabase.rpc('revoke_other_own_auth_sessions');
  if (error) throw error;

  // Belt-and-braces refresh-token cleanup — best-effort. The RPC's delete
  // already cascades refresh tokens, so a failure here must never fail the
  // action the user just saw succeed.
  try {
    const { error: signOutError } = await supabase.auth.signOut({ scope: 'others' });
    if (signOutError) console.warn('signOut(others):', signOutError.message);
  } catch (signOutErr) {
    console.warn('signOut(others):', signOutErr?.message || signOutErr);
  }

  return typeof data === 'number' ? data : (typeof count === 'number' ? count : 0);
}

export function buildSessionsSubLabel(sessions, formatMessage) {
  const count = Array.isArray(sessions) ? sessions.length : 0;
  if (!Array.isArray(sessions)) {
    return formatMessage({
      id: 'account.sessions.subLoading',
      defaultMessage: 'Devices signed in to your account',
    });
  }
  // An EMPTY array is never "this device" — the RPC always lists the caller's
  // own session, so zero rows means the read was gated or the session is gone.
  // Stay neutral; the expanded panel explains which.
  if (count <= 0) {
    return formatMessage({
      id: 'account.sessions.subLoading',
      defaultMessage: 'Devices signed in to your account',
    });
  }
  if (count === 1) {
    return formatMessage({
      id: 'account.sessions.subOne',
      defaultMessage: '1 active · this device only',
    });
  }
  const others = count - 1;
  return formatMessage(
    {
      id: 'account.sessions.subMany',
      defaultMessage: '{count} active · this device and {others, plural, one {# other} other {# others}}',
    },
    { count, others },
  );
}
